import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { SocialLink, PREDEFINED_SOCIAL_PLATFORMS } from './organizationTypes';

interface SocialLinksEditorProps {
  value: SocialLink[];
  onChange: (links: SocialLink[]) => void;
}

export function SocialLinksEditor({ value, onChange }: SocialLinksEditorProps) {
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customUrl, setCustomUrl] = useState('');

  const addPredefined = (platform: typeof PREDEFINED_SOCIAL_PLATFORMS[0]) => {
    if (value.find(l => l.platform === platform.key)) return;
    onChange([...value, { platform: platform.key, url: '', label: platform.label }]);
  };

  const removePlatform = (platform: string) => {
    onChange(value.filter(l => l.platform !== platform));
  };

  const updateUrl = (platform: string, url: string) => {
    onChange(value.map(l => l.platform === platform ? { ...l, url } : l));
  };

  const handlePredefinedBlur = (link: SocialLink) => {
    if (!link.url.trim() && PREDEFINED_SOCIAL_PLATFORMS.find(p => p.key === link.platform)) {
      removePlatform(link.platform);
    }
  };

  const addCustom = () => {
    if (!customLabel.trim() || !customUrl.trim()) return;
    const key = `custom_${Date.now()}`;
    onChange([...value, { platform: key, url: customUrl.trim(), label: customLabel.trim() }]);
    setCustomLabel('');
    setCustomUrl('');
    setShowCustomForm(false);
  };

  const cancelCustom = () => {
    setCustomLabel('');
    setCustomUrl('');
    setShowCustomForm(false);
  };

  return (
    <div className="space-y-4">
      {/* Platform chip row */}
      <div className="flex flex-wrap gap-2">
        {PREDEFINED_SOCIAL_PLATFORMS.map(p => {
          const active = !!value.find(l => l.platform === p.key);
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => active ? removePlatform(p.key) : addPredefined(p)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                active
                  ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
              }`}
            >
              {active && <span className="text-xs">✓</span>}
              {p.label}
            </button>
          );
        })}
        {!showCustomForm && (
          <button
            type="button"
            onClick={() => setShowCustomForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-dashed border-gray-400 text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Custom
          </button>
        )}
      </div>

      {/* URL inputs for added platforms */}
      {value.length > 0 && (
        <div className="space-y-3">
          {value.map(link => {
            const predefined = PREDEFINED_SOCIAL_PLATFORMS.find(p => p.key === link.platform);
            return (
              <div key={link.platform} className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 w-28 shrink-0 truncate">
                  {link.label}
                </span>
                <input
                  type="url"
                  value={link.url}
                  onChange={(e) => updateUrl(link.platform, e.target.value)}
                  onBlur={() => handlePredefinedBlur(link)}
                  placeholder={predefined?.placeholder || 'https://...'}
                  className="flex-1 px-3 py-2 border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                />
                <button
                  type="button"
                  onClick={() => removePlatform(link.platform)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title={`Remove ${link.label}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Custom platform inline form */}
      {showCustomForm && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <input
            type="text"
            placeholder="Label (e.g. My Blog)"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            className="flex-1 px-3 py-2 border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent"
          />
          <input
            type="url"
            placeholder="https://..."
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
            className="flex-1 px-3 py-2 border border-input rounded-lg text-sm focus:ring-2 focus:ring-ring focus:border-transparent"
          />
          <button
            type="button"
            onClick={addCustom}
            disabled={!customLabel.trim() || !customUrl.trim()}
            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Add
          </button>
          <button
            type="button"
            onClick={cancelCustom}
            className="px-3 py-2 text-gray-600 hover:text-gray-900 rounded-lg text-sm transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {value.length === 0 && !showCustomForm && (
        <p className="text-sm text-gray-400 italic">
          Click a platform above to add its link, or use Custom for other platforms.
        </p>
      )}
    </div>
  );
}



